import { useModelLabel } from "@judo/i18n";
import type { EnumerationRadio, Input } from "@judo/model-api";
import { getInputTestId } from "@judo/test-ids";
import { FormControl, FormControlLabel, FormHelperText, FormLabel, Radio, RadioGroup } from "@mui/material";

export interface EnumerationRadioComponentProps {
	element: EnumerationRadio | Input;
	readOnly?: boolean;
	value?: string;
	onChange?: (value: string) => void;
	error?: string;
	disabled?: boolean;
	options?: Array<{ value: string; label: string }>;
	/** Layout orientation of radio buttons. @default "vertical" */
	orientation?: "horizontal" | "vertical";
	/** When true, allows de-selection by clicking a selected radio. @default false */
	nullable?: boolean;
}

/**
 * Enumeration radio group component.
 */
export function EnumerationRadioComponent({
	element,
	readOnly,
	value = "",
	onChange,
	error,
	disabled,
	options = [],
	orientation = "vertical",
	nullable = false,
}: EnumerationRadioComponentProps) {
	const testId = getInputTestId(element);
	const { label } = useModelLabel(element);

	const handleRadioClick = (optionValue: string) => {
		// If nullable and clicking the already-selected option, deselect it
		if (nullable && value === optionValue) {
			onChange?.("");
		} else {
			onChange?.(optionValue);
		}
	};

	return (
		<FormControl error={!!error} disabled={disabled ?? element.disabled} required={element.required}>
			<FormLabel>{label}</FormLabel>
			<RadioGroup value={value} row={orientation === "horizontal"} data-testid={testId}>
				{options.map((option) => (
					<FormControlLabel
						key={option.value}
						value={option.value}
						control={<Radio disabled={readOnly ?? element.isReadOnly} onClick={() => handleRadioClick(option.value)} />}
						label={option.label}
					/>
				))}
			</RadioGroup>
			{error && <FormHelperText>{error}</FormHelperText>}
		</FormControl>
	);
}
